import { IconWrapper, IStyles, pxToRem, pxTovW } from '@geneo2-web/shared-ui';
import { Box, Typography } from '@mui/material';
import { AssessmentStudentResponseV2 } from '@protos/learning_management/lms.assessment.student.apis_pb';
import { TaskEnum } from '@protos/learning_management/lms.db_pb';
import { getSubjectNameFromEnum } from '../../utils_assessment/assessmentUtils';

const styles: IStyles = {
  root: {
    width: '100%',
    mt: { xs: pxToRem(18), md: pxTovW(26) },
    px: { xs: pxToRem(4), md: 0 },
  },
  list: {
    mt: { xs: pxToRem(10), md: pxTovW(14) },
    maxHeight: { xs: pxToRem(320), md: pxTovW(520) },
    overflowY: 'auto',
    pr: { xs: pxToRem(4), md: pxTovW(6) },
  },
  card: {
    position: 'relative',
    mb: { xs: pxToRem(10), md: pxTovW(12) },
    p: {
      xs: `${pxToRem(20)} ${pxToRem(20)}`,
      md: `${pxTovW(25)} ${pxTovW(30)}`,
    },
    borderRadius: { xs: pxToRem(12), md: pxTovW(15) },
    backgroundColor: '#FFFFFF',
    boxShadow: `0px 0px ${pxTovW(10)} #0000001F`,
  },
  chip: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: '#CCE8FF',
    color: '#155D8A',
    borderBottomLeftRadius: { xs: pxToRem(8), md: pxTovW(8) },
    borderTopRightRadius: { xs: pxToRem(10), md: pxTovW(10) },
    px: { xs: pxToRem(10), md: pxTovW(10) },
    py: { xs: pxToRem(3), md: pxTovW(3) },
    maxWidth: { xs: '68%', md: '62%' },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  metricsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(8), md: pxTovW(10) },
    pt: { xs: pxToRem(18), md: pxTovW(22) },
  },
  metricsRowOmr: {
    display: 'flex',
    justifyContent: 'space-evenly',
    gap: { xs: pxToRem(8), md: pxTovW(10) },
    pt: { xs: pxToRem(18), md: pxTovW(22) },
  },
  singleCard: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(8), md: pxTovW(10) },
    p: {
      xs: `${pxToRem(20)} ${pxToRem(20)}`,
      md: `${pxTovW(25)} ${pxTovW(30)}`,
    },
    borderRadius: { xs: pxToRem(12), md: pxTovW(15) },
    backgroundColor: '#FFFFFF',
    boxShadow: `0px 0px ${pxTovW(10)} #0000001F`,
    mt: { xs: pxToRem(10), md: pxTovW(10) },
    boxSizing: 'border-box',
    width: { xs: '96%', md: 'unset' },
  },
};

const multiSubjectTaskTypes = new Set<TaskEnum>([
  TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM,
]);

const singleSubjectTaskTypes = new Set<TaskEnum>([
  TaskEnum.TASK_FORMATIVE_ASSESSMENT_AUTOMATED,
  TaskEnum.TASK_FORMATIVE_ASSESSMENT_CUSTOM,
]);

interface IProps {
  taskType?: TaskEnum;
  studentResponse?: AssessmentStudentResponseV2;
  isOmrAssessment: boolean;
}

export const AssessOverallSummary = ({
  taskType,
  studentResponse,
  isOmrAssessment,
}: IProps) => {
  const metricsRowStyles = isOmrAssessment
    ? styles.metricsRowOmr
    : styles.metricsRow;

  // ----------------------------------------------------------

  const formatDurationInMinutes = (timeSpentInSec = 0) => {
    if (timeSpentInSec < 60) {
      return (timeSpentInSec / 60).toFixed(2);
    }

    return `${Math.floor(timeSpentInSec / 60)}`;
  };

  // ----------------------------------------------------------

  if (!taskType || !studentResponse) {
    return null;
  }

  if (
    multiSubjectTaskTypes.has(taskType) &&
    (studentResponse.multiSubjectMetrics?.length ?? 0) > 0
  ) {
    return (
      <Box sx={styles.root}>
        <Typography variant="h2" fontWeight="bold">
          Overall Summary
        </Typography>

        <Box sx={styles.list}>
          {studentResponse.multiSubjectMetrics.map((section) => (
            <Box key={section.sectionNo} sx={styles.card}>
              <Box sx={styles.chip}>
                <Typography variant="smallText" fontWeight={600}>
                  {`${getSubjectNameFromEnum(section.subject)}${
                    section.sectionTitle ? ` - ${section.sectionTitle}` : ''
                  }`}
                </Typography>
              </Box>

              <Box sx={metricsRowStyles}>
                <SummaryMetric
                  icon="clipboard-blue"
                  boldText={`${section.classSubjectScore}`}
                  text={`${section.classSubjectMaxScore}`}
                  label="Marks"
                />
                <SummaryMetric
                  icon="green-circle-check"
                  boldText={`${section.numberOfCorrectQuestions}`}
                  text={`${section.numberOfQuestions}`}
                  label="Correct"
                />
                {!isOmrAssessment && (
                  <SummaryMetric
                    icon="clock"
                    boldText={`${section.timeDurationInMin}`}
                    label="Minutes"
                  />
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // if (singleSubjectTaskTypes.has(taskType) ) {
  if (
    studentResponse.metrics?.responseScore &&
    studentResponse.metrics?.maxScore &&
    studentResponse.metrics?.correctCount &&
    studentResponse.metrics?.totalQuestions &&
    studentResponse.metrics?.timeSpentInSec
  ) {
    return (
      <Box sx={styles.root}>
        <Typography variant="h2" fontWeight="bold">
          Overall Summary
        </Typography>

        <Box
          sx={{
            ...styles.singleCard,
            justifyContent: isOmrAssessment ? 'space-evenly' : 'space-between',
          }}
        >
          <SummaryMetric
            icon="clipboard-blue"
            boldText={`${studentResponse.metrics?.responseScore}`}
            text={`${studentResponse.metrics?.maxScore}`}
            label="Marks"
          />
          <SummaryMetric
            icon="green-circle-check"
            boldText={`${studentResponse.metrics?.correctCount}`}
            text={`${studentResponse.metrics?.totalQuestions}`}
            label="Correct"
          />
          {!isOmrAssessment && (
            <SummaryMetric
              icon="clock"
              boldText={formatDurationInMinutes(
                studentResponse.metrics?.timeSpentInSec
              )}
              label="Minutes"
            />
          )}
        </Box>
      </Box>
    );
  }

  return null;
};

// ==============================================================================
// Summary Metric Component
// ==============================================================================

interface ISummaryMetric {
  icon: string;
  boldText: string;
  text?: string;
  label: string;
}

const SummaryMetric = ({ icon, boldText, text, label }: ISummaryMetric) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: pxToRem(5), md: pxTovW(8) },
        }}
      >
        <IconWrapper name={icon} size="md" parentFolder="icons" type="png" />
        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
          <Typography variant="h2" fontWeight="bold">
            {boldText}
          </Typography>
          {text ? (
            <Typography variant="h3" color="text.disabled">
              /{text}
            </Typography>
          ) : null}
        </Box>
      </Box>
      <Typography variant="h3" color="text.disabled">
        {label}
      </Typography>
    </Box>
  );
};

// ==============================================================================
